import React, { useEffect, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { AppBottomSheet, Button, Chip, SearchableSelect, Text } from '~/components/core';
import { SelectOption } from '~/components/core/SearchableSelect';
import { StrategyStatus } from '~/api-services/AIStrategyBuilderService/types';
import { periodLabel, statusLabel, STRATEGY_PERIODS } from '~/constants/strategyConstants';
import { useStyles } from '~/hooks';
import { useTheme } from '~/hooks/useTheme';
import { FONT_SIZE_S, FONT_SIZE_XS } from '~/constants/typography';
import { scaleSize } from '~/constants/scale';
import { Theme } from '~/theme';

import { StrategyListFilters } from '../../hooks/useStrategies';
import { composeSort, parseSort, SORT_FIELDS, sortDirLabelKey, sortFieldLabelKey } from '../../utils/sort';

const ALL_STATUSES: StrategyStatus[] = [
    'created',
    'inprogress',
    'done',
    'seen',
    'enabled',
    'disabled',
    'failed',
    'discarded',
];

type Props = {
    visible: boolean;
    onClose: () => void;
    filters: StrategyListFilters;
    onApply: (filters: StrategyListFilters) => void;
    instrumentOptions: SelectOption[];
};

// Filter sheet: single-select status (the API takes exactly one) incl. the archived-only view,
// instrument, period, and sort split into a field group + a direction group (utils/sort.ts).
export default function StrategyFilterSheet({ visible, onClose, filters, onApply, instrumentOptions }: Props) {
    const styles = useStyles(stylesGenerator);
    const theme = useTheme();
    const [draft, setDraft] = useState<StrategyListFilters>(filters);
    const [instrumentPickerOpen, setInstrumentPickerOpen] = useState(false);

    useEffect(() => {
        if (visible) {
            setDraft(filters);
        }
    }, [visible, filters]);

    const instrumentLabel =
        instrumentOptions.find((o) => o.value === draft.instrument_code)?.label ?? draft.instrument_code;
    const sort = parseSort(draft.sort);

    return (
        <AppBottomSheet visible={visible} onClose={onClose} title="filters">
            <Text fontType="LATO_BOLD" style={styles.groupTitle}>
                status
            </Text>
            <View style={styles.chipsWrap}>
                <Chip
                    label="all"
                    selected={!draft.status && !draft.archivedOnly}
                    onPress={() => setDraft((d) => ({ ...d, status: undefined, archivedOnly: undefined }))}
                    containerStyle={styles.chip}
                />
                {ALL_STATUSES.map((status) => (
                    <Chip
                        key={status}
                        label={statusLabel(status)}
                        selected={draft.status === status}
                        onPress={() => setDraft((d) => ({ ...d, status, archivedOnly: undefined }))}
                        containerStyle={styles.chip}
                    />
                ))}
                <Chip
                    label="ai-strategy-archived"
                    selected={!!draft.archivedOnly}
                    onPress={() => setDraft((d) => ({ ...d, status: undefined, archivedOnly: true }))}
                    containerStyle={styles.chip}
                />
            </View>

            <Text fontType="LATO_BOLD" style={styles.groupTitle}>
                ai-strategy-sort
            </Text>
            <View style={styles.chipsWrap}>
                {SORT_FIELDS.map((field) => (
                    <Chip
                        key={field}
                        label={sortFieldLabelKey(field)}
                        selected={sort.field === field}
                        // Re-parse inside the updater — the render-time `sort` may lag `d.sort`
                        // when field and direction change before a re-render.
                        onPress={() => setDraft((d) => ({ ...d, sort: composeSort(field, parseSort(d.sort).dir) }))}
                        containerStyle={styles.chip}
                    />
                ))}
            </View>

            <Text fontType="LATO_BOLD" style={styles.groupTitle}>
                ai-strategy-sort-direction
            </Text>
            <View style={styles.chipsWrap}>
                {(['desc', 'asc'] as const).map((dir) => (
                    <Chip
                        key={dir}
                        label={sortDirLabelKey(sort.field, dir)}
                        selected={sort.dir === dir}
                        onPress={() => setDraft((d) => ({ ...d, sort: composeSort(parseSort(d.sort).field, dir) }))}
                        containerStyle={styles.chip}
                    />
                ))}
            </View>

            <Text fontType="LATO_BOLD" style={styles.groupTitle}>
                side
            </Text>
            <View style={styles.chipsWrap}>
                <Chip
                    label="all"
                    selected={!draft.side}
                    onPress={() => setDraft((d) => ({ ...d, side: undefined }))}
                    containerStyle={styles.chip}
                />
                <Chip
                    label="buy"
                    selected={draft.side === 'buy'}
                    onPress={() => setDraft((d) => ({ ...d, side: 'buy' }))}
                    containerStyle={styles.chip}
                />
                <Chip
                    label="sell"
                    selected={draft.side === 'sell'}
                    onPress={() => setDraft((d) => ({ ...d, side: 'sell' }))}
                    containerStyle={styles.chip}
                />
            </View>

            <Text fontType="LATO_BOLD" style={styles.groupTitle}>
                instrument
            </Text>
            <Pressable style={styles.instrumentRow} onPress={() => setInstrumentPickerOpen(true)}>
                <Text noTranslate={!!draft.instrument_code} style={styles.instrumentValue}>
                    {draft.instrument_code ? instrumentLabel ?? '' : 'all'}
                </Text>
            </Pressable>
            {draft.instrument_code ? (
                <Pressable onPress={() => setDraft((d) => ({ ...d, instrument_code: undefined }))}>
                    <Text style={styles.clearLink}>ai-strategy-filter-clear-instrument</Text>
                </Pressable>
            ) : null}

            <Text fontType="LATO_BOLD" style={styles.groupTitle}>
                period
            </Text>
            <View style={styles.chipsWrap}>
                <Chip
                    label="all"
                    selected={!draft.instrument_period}
                    onPress={() => setDraft((d) => ({ ...d, instrument_period: undefined }))}
                    containerStyle={styles.chip}
                />
                {STRATEGY_PERIODS.map((p) => (
                    <Chip
                        key={p}
                        label={periodLabel(p)}
                        selected={draft.instrument_period === p}
                        onPress={() => setDraft((d) => ({ ...d, instrument_period: p }))}
                        containerStyle={styles.chip}
                    />
                ))}
            </View>

            <View style={styles.buttonsRow}>
                <View style={styles.buttonWrap}>
                    <Button
                        title="reset"
                        onPress={() => {
                            onApply({});
                            onClose();
                        }}
                        backgroundColor={{ backgroundColor: theme.backgroundSecondary }}
                        titleColor={theme.text}
                    />
                </View>
                <View style={styles.buttonWrap}>
                    <Button
                        title="apply"
                        onPress={() => {
                            onApply(draft);
                            onClose();
                        }}
                    />
                </View>
            </View>

            <SearchableSelect
                visible={instrumentPickerOpen}
                onClose={() => setInstrumentPickerOpen(false)}
                title="instrument"
                options={instrumentOptions}
                selectedValue={draft.instrument_code}
                onSelect={(value) => setDraft((d) => ({ ...d, instrument_code: value }))}
            />
        </AppBottomSheet>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        groupTitle: {
            fontSize: FONT_SIZE_S,
            color: theme.text,
            marginTop: scaleSize(10),
            marginBottom: scaleSize(8),
        },
        chipsWrap: {
            flexDirection: 'row',
            flexWrap: 'wrap',
        },
        chip: {
            marginRight: scaleSize(8),
            marginBottom: scaleSize(8),
        },
        instrumentRow: {
            borderWidth: 1,
            borderColor: theme.equityValue,
            borderRadius: scaleSize(8),
            backgroundColor: theme.backgroundSecondary,
            paddingHorizontal: scaleSize(10),
            paddingVertical: scaleSize(10),
        },
        instrumentValue: {
            fontSize: FONT_SIZE_S,
            color: theme.text,
        },
        clearLink: {
            fontSize: FONT_SIZE_XS,
            color: theme.button,
            marginTop: scaleSize(6),
        },
        buttonsRow: {
            flexDirection: 'row',
            marginTop: scaleSize(16),
            marginBottom: scaleSize(4),
        },
        buttonWrap: {
            flex: 1,
            marginHorizontal: scaleSize(4),
        },
    });
